import { Component, Input, OnInit } from '@angular/core';
import { BaseComponent, RouterService, SearchResult } from 'src/app/shared';
import { Policies } from 'src/app/shared/models/Policies';
import { ClaimService } from '../../services/claim.service';
import { Router } from '@angular/router';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';
@Component({
    selector: 'app-leave',
    template: `
    <app-loading-panel [state]="isLoaded ? 'Loaded' : 'Loading'">
      <ng-template #content>
        <app-list
          #table
          [dataSource]="policiesInfo"
          [template]="row"
          class="app-pt2"
          [filterEnabled]="false"
          [paginatorEnabled]="false"
          loadingMessage=""
          noDataIcon="list_alt"
          noDataMessage="You do not have any leave balance information."
          [tableAriaLabel]="'Leaves'"
          >
          <ng-container header>
            <tr class="tw-flex tw-flex-wrap tw-w-full">
              <app-list-header
                name="policyName"
                label="Policy"
                class="tw-w-full md:tw-w-[40%]"
                [names]="['policyName']"
              ></app-list-header>
              <app-list-header
                name="timeUsed"
                label="Time Used"
                class="tw-w-full md:tw-w-[30%]"
                [names]="['timeUsed']"
                [labelTemplate]="labelTemplate"
                >
                <ng-template #labelTemplate>
                  <app-help-button
                    alt="Column information"
                    (click)="$event.stopPropagation()"
                    [size]="20"
                    message="Time Used is based on how much time has been used for this claim only, as of today."
                    [id]="'leaveToolTip'"
                    >
                  </app-help-button>
                </ng-template>
              </app-list-header>
    
              <app-list-header
                name="exhaustionDate"
                label="Policy Exhausts"
                class="tw-w-full md:tw-w-[30%]"
                [names]="['exhaustionDate']"
              ></app-list-header>
            </tr>
          </ng-container>
        </app-list>
    
        <ng-template #row let-element>
          <tr class="tw-flex tw-flex-wrap tw-w-full tw-items-center">
            <td class="tw-w-full md:tw-w-[40%]">
              <app-value
                label="Policy"
                [value]="element.policyName"
                [tabIndex]="0"
                layout="row-xs"
                >
              </app-value>
            </td>
            <td class="tw-w-full md:tw-w-[30%]">
              <app-value
                label="Time Used"
                [tabIndex]="0"
                [value]="claim?.source !== 'L' ? element.timeUsed + ' ' + (element.timeUnit | translation: translationRequest) : element.timeUsed"
                layout="row-xs"
                [translationNeeded]="false"
                >
              </app-value>
            </td>
            <td class="tw-w-full md:tw-w-[30%]">
              <app-value
                label="Policy Exhausts"
                [tabIndex]="0"
                [value]="element.exhaustionDate | format : 'date'"
                [translationNeeded]="false"
                layout="row-xs"
                >
              </app-value>
            </td>
          </tr>
        </ng-template>
        @if (showLabels) {
          <div class="summary">
            <img
              [src]="'/assets/images/welcome.png'"
              alt=""
              height="100"
              width="100"
              />
              <ul>
                @if(claim?.source !== 'L'){
                  <li>
                    {{ 'You can also' | translation : translationRequest }}
                    <a
                      href=" https://view.ceros.com/sedgwick/leave-law-map-updated/p/1"
                      target="_blank"
                      (click)="handleClick('leaveBalanceSummary1')"
                      >
                      {{ 'learn more' | translation : translationRequest }}
                    </a>
                    {{ 'about leave rights in your state' | translation : translationRequest }}
                  </li>
                }
                <li>
                  {{ 'View your' | translation : translationRequest }}
                  <a
                    href="javascript:void(0)"
                    (click)="handleClick('leaveBalanceSummary')"
                [queryParams]="{
                  fromClaim: 'yes',
                  claimId: this.claim.claimId,
                  source: this.claim.source
                }"
                    routerLink="/leaveBalanceSummary"
                    >
                    {{ 'leave balance summary' | translation : translationRequest }}
                  </a>
                  {{ 'for all of your related claims' | translation : translationRequest }}
                </li>
              </ul>
            </div>
          }
        </ng-template>
      </app-loading-panel>
    `,
    styles: [
        `
      .summary {
        display: flex;
        gap: 40px;
        align-items: center;
        padding: 7px;
      }
      li {
        list-style-type: disc;
      }
    `,
    ],
    standalone: false
})
export class LeaveComponent extends BaseComponent implements OnInit {
  @Input()
  claim: SearchResult;
  source: any;
  isLoaded: boolean = false;
  policiesInfo: Policies[];
  showLabels: boolean = false;

  @Input()
  translationRequest: TranslationRequest;

  constructor(
    public routerService: RouterService,
    public claimService: ClaimService,
    private router: Router
  ) {
    super();
  }

  async ngOnInit() {
    // Validate all required fields before making API call to prevent ModelBindingError
    if (
      this?.claim?.claimNumber &&
      this?.claim?.claimId &&
      this?.claim?.source
    ) {
      const result = await this.claimService.getPolicies(
        this.claim.claimNumber,
        this.claim.claimId,
        this.claim.source
      );
      if (result) {
        this.policiesInfo = result;
        this.showLabels = true;
      }
      this.isLoaded = true;
    } else {
      // Log missing required fields for debugging
      if (!this?.claim?.claimNumber) {
        console.warn(
          'LeaveComponent: claimNumber (FileNumber) is missing or null'
        );
      }
      if (!this?.claim?.claimId) {
        console.warn('LeaveComponent: claimId is missing or null');
      }
      if (!this?.claim?.source) {
        console.warn('LeaveComponent: source is missing or null');
      }
      this.isLoaded = true;
    }
  }

  handleClick(featureName: string) {
    if (featureName == 'leaveBalanceSummary') {
      this.router.navigate(['/leaveBalanceSummary'], {
        queryParams: {
          fromClaim: 'yes',
          claimId: this.claim.claimId,
          source: this.claim.source,
        },
      });

      this.logFeature('LeaveBalanceSummary', 'View - Leave Balance Summary');
    } else {
      this.logFeature(
        'LeaveBalanceLearnMore',
        'Leave Balance Summary - Learn More'
      );
    }
  }

  logFeature(featureName: string, sectionName: string) {
    this.FeatureLoggingWithClaim(featureName, null, sectionName, this.claim);
  }
}
